import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import ViewportProvider from '@cobalt/react-viewport-provider';
import ThemeProvider, { useTheme } from '@cobalt/react-theme-provider';
import { v4 as uuidv4 } from 'uuid';
import { IToastConfig } from '@atlas/sdk/lib/namespaces/toast/toast.types';

import Box from '@cobalt/react-box';
import Flex from '@cobalt/react-flex';
import Icon, { Name } from '@cobalt/react-icon';
import { Text } from '@cobalt/react-typography';
import Button from '@cobalt/react-button';
import AtlasSdk from '@atlas/sdk';

let customNotificationList: CustomNotificationItemProps[] = [];
let rootDiv: HTMLDivElement;

export interface CustomNotificationItemProps extends IToastConfig {
  onClose?: (_id?: string, isBtn?: boolean) => void;
  titleIcon?: Name;
  showClose?: boolean;
  _id?: string;
  index?: number;
  style?: React.CSSProperties;
  space?: React.CSSProperties['height'];
  last?: boolean;
  btnInfo?: React.ReactNode;
  marginRight?: React.CSSProperties['marginRight'];
  animateTime?: number;
  themeName?: 'default' | 'ghost';
}

export interface CustomNotificationProps {
  list: CustomNotificationItemProps[];
  onClose?: (_id?: string, isBtn?: boolean) => void;
}

export type CustomNotificationOptions = Omit<CustomNotificationItemProps, 'type' | 'last' | '_id' | 'dismissible'>;

export interface ShowCustomNotificationParams {
  config: CustomNotificationItemProps;
  foreverShowLast: boolean;
}

export interface CustomNotificationOptionsParams {
  config: CustomNotificationOptions;
  /**
   * Whether to enable the overlay only show the notification of the last call
   */
  foreverShowLast?: boolean;
}

export interface CustomNotificationStyle {
  color: React.CSSProperties['color'];
  backgroundColor: React.CSSProperties['color'];
}

export function CustomNotification(props: CustomNotificationProps) {
  const { list, onClose } = props;
  const [recordList, setRecordList] = useState<CustomNotificationItemProps[]>([]);

  useEffect(() => {
    setRecordList(list);
  }, [list]);

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        bottom: 20,
        zIndex: 1400,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'end',
        }}
      >
        {recordList.map((record, index: number) => (
          <CustomNotificationItem
            {...record}
            key={record._id}
            last={index === recordList.length - 1}
            onClose={(_id, isBtn) => onClose?.(_id, isBtn)}
          />
        ))}
      </div>
    </div>
  );
}

export function CustomNotificationItem(props: CustomNotificationItemProps) {
  const {
    _id,
    title,
    titleIcon = 'info',
    message,
    type = 'info',
    showClose = true,
    onClose,
    style = {
      width: 600,
      borderRadius: 4,
    },
    space = 12,
    last,
    btnInfo,
    marginRight = 20,
    animateTime = 300,
    dismissDelay = AtlasSdk.toast.delays.INFINITE,
    themeName = 'default',
  } = props;
  const theme = useTheme();
  const primaryThemeMapper: Record<string, Record<string, CustomNotificationStyle>> = {
    info: {
      default: {
        color: theme.white,
        backgroundColor: theme.blue600,
      },
      ghost: {
        color: theme.blue600,
        backgroundColor: theme.blue200,
      },
    },
    danger: {
      default: {
        color: theme.white,
        backgroundColor: theme.red600,
      },
      ghost: {
        color: theme.red600,
        backgroundColor: theme.red200,
      },
    },
    warning: {
      default: {
        color: theme.white,
        backgroundColor: theme.orange600,
      },
      ghost: {
        color: theme.orange600,
        backgroundColor: theme.orange200,
      },
    },
    success: {
      default: {
        color: theme.white,
        backgroundColor: theme.green600,
      },
      ghost: {
        color: theme.green600,
        backgroundColor: theme.green200,
      },
    },
  };
  const primaryTheme = primaryThemeMapper[type][themeName];
  const dismissDelayMapper = {
    [AtlasSdk.toast.delays.SHORT]: 3000,
    [AtlasSdk.toast.delays.MEDIUM]: 6000,
    [AtlasSdk.toast.delays.LONG]: 9000,
    [AtlasSdk.toast.delays.INFINITE]: -1,
  };
  const timer = dismissDelayMapper[dismissDelay];
  const boxRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const animateTimerRef = useRef<number | null>(null);
  const [buttonHover, setButtonHover] = useState(false);

  const emitOnClose = (isBtn = false) => {
    clearTimer();
    animateTimerRef.current = window.setTimeout(() => {
      onClose?.(_id, isBtn);
    }, animateTime);
    if (boxRef.current) boxRef.current.style.transform = 'translate(100%, 0)';
  };

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    emitOnClose(true);
  };

  const clearAnimateTimer = () => {
    if (animateTimerRef.current) {
      clearTimeout(animateTimerRef.current);
      animateTimerRef.current = null;
    }
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    if (timer <= 0) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      emitOnClose(false);
    }, timer);
  };

  useEffect(() => {
    startTimer();
    return () => {
      clearAnimateTimer();
      clearTimer();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div style={{ marginRight }}>
      <Box
        style={{
          pointerEvents: 'auto',
          backgroundColor: primaryTheme.backgroundColor,
          ...style,
          transform: 'translate(0, 0)',
          transition: `transform ${animateTime}ms`,
        }}
        padding="3"
        forwardedRef={boxRef}
      >
        <Flex width="100%" alignY="center" alignX="space-between">
          {(title || titleIcon) && (
            <Flex alignY="center">
              {titleIcon && <Icon name={titleIcon} size="tiny" color={primaryTheme.color} />}
              {title && (
                <Text
                  style={{ paddingLeft: 4, paddingRight: 4 }}
                  color={primaryTheme.color}
                  weight="medium"
                  size="medium"
                  truncated
                  title={title}
                  data-testid="custom-notification-title"
                >
                  {title}
                </Text>
              )}
            </Flex>
          )}
          {showClose && (
            <Flex alignY="center" alignX="end">
              <Icon name="close" color={primaryTheme.color} size="tiny" onClick={() => emitOnClose(false)} />
            </Flex>
          )}
        </Flex>
        {typeof message === 'string' ? (
          <Box paddingLeft="6" paddingRight="6" paddingTop="1" paddingBottom="2">
            <Text
              title={message}
              color={primaryTheme.color}
              style={{ wordBreak: 'break-word' }}
              data-testid="custom-notification-message"
            >
              {message}
            </Text>
          </Box>
        ) : (
          message
        )}
        {btnInfo && (
          <Box paddingLeft="6">
            <Button
              onMouseEnter={() => setButtonHover(true)}
              onMouseLeave={() => setButtonHover(false)}
              size="small"
              variation="outline"
              style={{
                borderColor: primaryTheme.color,
                backgroundColor: buttonHover ? primaryTheme.backgroundColor : 'transparent',
                color: primaryTheme.color,
              }}
              onClick={handleOnClick}
            >
              {typeof btnInfo === 'string' ? <Text>{btnInfo}</Text> : btnInfo}
            </Button>
          </Box>
        )}
      </Box>
      {<div style={{ backgroundColor: 'transparent', width: '100%', height: !!last ? 0 : space }} />}
    </div>
  );
}

export const showCustomNotification = ({ config, foreverShowLast }: ShowCustomNotificationParams) => {
  if (!rootDiv) {
    rootDiv = document.createElement('div') as HTMLDivElement;
    document.body.appendChild(rootDiv);
  }

  config._id = uuidv4();
  customNotificationList = foreverShowLast ? [config] : [...customNotificationList, config];

  const handleOnClose = (_id?: string, isBtn?: boolean) => {
    if (!_id) return;
    customNotificationList = customNotificationList.filter((record) => record._id !== _id);
    renderCustomNotificationList(customNotificationList, handleOnClose);
    config.onClose?.(_id, isBtn);
  };

  renderCustomNotificationList(customNotificationList, handleOnClose);
};

export const clearCustomNotification = () => {
  customNotificationList = [];
  renderCustomNotificationList([]);
};

function renderCustomNotificationList(
  list: CustomNotificationItemProps[],
  onClose?: CustomNotificationItemProps['onClose'],
) {
  ReactDOM.render(
    <ViewportProvider>
      <ThemeProvider>
        <CustomNotification list={list} onClose={onClose} />
      </ThemeProvider>
    </ViewportProvider>,
    rootDiv,
  );
}
